import { useState } from 'react'
import {ArrowRight, Check, ShieldCheck, Clock, FileText} from 'lucide-react'
import { services } from '@/data/services'

function Quote() {
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({
    name: '', company: '', email: '',
    propertyType: '', squareFootage: '', servicesNeeded: [] as string[],
    message: '',
  })

  const toggleService = (title: string) => {
    setForm(prev => ({
      ...prev,
      servicesNeeded: prev.servicesNeeded.includes(title)
        ? prev.servicesNeeded.filter(s => s !== title)
        : [...prev.servicesNeeded, title],
    }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (submitted) {
    return (
      <section className="min-h-[70vh] flex items-center py-24">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-orange-accent/10 flex items-center justify-center mb-8">
            <Check className="w-7 h-7 text-orange-accent" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-navy leading-tight">Thank you, {form.name.split(' ')[0] || 'friend'}.</h1>
          <p className="mt-6 text-muted text-lg leading-relaxed">
            Your custom maintenance plan request is in our queue. A Delta specialist will contact you within <strong className="text-navy">one business hour</strong> to discuss your property needs.
          </p>
          <a href="/" className="mt-10 inline-flex items-center gap-2 text-orange-accent font-semibold link-underline">
            Back to Home <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    )
  }

  return (
    <div>
      {/* HERO */}
      <section className="bg-navy text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-orange-accent" />
              <span className="text-xs uppercase tracking-[0.3em] text-orange-accent font-semibold">Request a Quote</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-bold leading-[1.05]">
              Get a custom <span className="text-orange-accent">maintenance plan.</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed">
              Tell us about your property. In one business hour, we'll respond with a tailored proposal.
            </p>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="py-20 md:py-28 bg-grey-soft">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-8 bg-white border border-soft rounded-2xl p-8 md:p-10 shadow-sm">
            <h2 className="font-display text-2xl font-bold text-navy mb-2">Your details</h2>
            <p className="text-muted text-sm mb-8">All fields marked with * are required.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-wider text-navy font-semibold mb-2">Full Name *</label>
                <input required name="name" value={form.name} onChange={handleChange}
                  className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-navy font-semibold mb-2">Company *</label>
                <input required name="company" value={form.company} onChange={handleChange}
                  className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-navy font-semibold mb-2">Work Email *</label>
                <input required type="email" name="email" value={form.email} onChange={handleChange}
                  className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition" />
              </div>
            </div>

            <h3 className="font-display text-lg font-bold text-navy mb-4 mt-8">Property information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-wider text-navy font-semibold mb-2">Property Type *</label>
                <select required name="propertyType" value={form.propertyType} onChange={handleChange}
                  className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition bg-white">
                  <option value="">Select property type</option>
                  <option>Commercial Office</option>
                  <option>Residential Strata</option>
                  <option>Retail / Shopping Centre</option>
                  <option>Industrial / Warehouse</option>
                  <option>Hospitality / Hotel</option>
                  <option>Mixed-Use</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-navy font-semibold mb-2">Square Footage *</label>
                <select required name="squareFootage" value={form.squareFootage} onChange={handleChange}
                  className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition bg-white">
                  <option value="">Select range</option>
                  <option>Under 10,000 sq ft</option>
                  <option>10,000 – 50,000 sq ft</option>
                  <option>50,000 – 100,000 sq ft</option>
                  <option>100,000 – 500,000 sq ft</option>
                  <option>Over 500,000 sq ft</option>
                  <option>Multi-property portfolio</option>
                </select>
              </div>
            </div>

            <h3 className="font-display text-lg font-bold text-navy mb-4 mt-8">Services needed *</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {services.map((s) => {
                const active = form.servicesNeeded.includes(s.title)
                return (
                  <button
                    key={s.id} type="button" onClick={() => toggleService(s.title)}
                    className={`text-left px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                      active
                        ? 'border-orange-accent bg-orange-accent/5 text-navy'
                        : 'border-soft text-navy hover:border-navy/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center flex-shrink-0 ${active ? 'border-orange-accent bg-orange-accent' : 'border-gray-300'}`}>
                        {active && <Check className="w-3 h-3 text-white" />}
                      </div>
                      {s.title}
                    </div>
                  </button>
                )
              })}
            </div>

            <div className="mb-8">
              <label className="block text-xs uppercase tracking-wider text-navy font-semibold mb-2">Additional details</label>
              <textarea name="message" value={form.message} onChange={handleChange} rows={4}
                placeholder="Tell us about your timeline, current provider, specific pain points..."
                className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition resize-none" />
            </div>

            <button
              type="submit"
              className="w-full bg-orange-accent hover:bg-orange-dark text-white py-4 rounded-lg font-semibold text-base tracking-wide transition-all flex items-center justify-center gap-2 group shadow-lg shadow-orange-accent/30"
            >
              Get a Custom Maintenance Plan
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-center text-xs text-muted mt-4">Your information stays confidential. We don't share details with third parties.</p>
          </form>

          {/* Side info */}
          <aside className="lg:col-span-4 space-y-5">
            <div className="bg-navy text-white rounded-2xl p-7">
              <h3 className="font-display text-xl font-bold mb-5">What happens next?</h3>
              <div className="space-y-5">
                {[
                  { icon: Clock, title: 'Within 1 hour', desc: 'A Delta specialist reviews your request and reaches out.' },
                  { icon: FileText, title: 'Within 48 hours', desc: 'Receive a detailed proposal customized to your property.' },
                  { icon: ShieldCheck, title: 'No obligation', desc: 'Review, compare, and decide on your timeline.' },
                ].map((step, i) => {
                  const Icon = step.icon
                  return (
                    <div key={i} className="flex gap-4">
                      <div className="w-9 h-9 rounded-lg bg-orange-accent/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-orange-accent" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{step.title}</div>
                        <div className="text-white/70 text-sm leading-relaxed mt-0.5">{step.desc}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="bg-white border border-soft rounded-2xl p-7">
              <div className="text-xs uppercase tracking-[0.2em] text-orange-accent font-semibold mb-3">Prefer email?</div>
              <a href="mailto:info@deltafacilitiesservices.com" className="font-display text-xl font-bold text-navy hover:text-orange-accent transition-colors break-all">info@deltafacilitiesservices.com</a>
              <div className="text-muted text-sm mt-1">Replies within 1 business hour</div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  )
}

export default Quote
